import { createEffect, Show, type JSX } from 'solid-js'
import { ViewSlot, type ViewProps } from 'wdc-cube-solid'
import type { MainScope } from 'mormaii-web-presentation/main'

import { AlertView } from '../../components/dialog'
import { Brand } from '../../components/icons'

import { applyTheme } from '../../lib/apply-theme'

import Css from './main.module.scss'

/**
 * A casca da aplicacao.
 *
 * Desenha o corpo que o `MainPresenter` pendurou no slot, mais o dialogo e o aviso,
 * que vao por cima dele.
 */
export function MainView(props: ViewProps<MainScope>): JSX.Element {
    // A aparencia escolhida vai para a raiz da pagina - o tema e da pagina inteira, e
    // nao de uma view. Sem sessao o scope diz Praia.
    // So depois de inicializar: antes disso o scope ainda diz Praia, e aplicar desfaria
    // o palpite do ultimo uso (`applyCachedTheme`, em `main.tsx`).
    createEffect(() => {
        if (props.scope.initialized) {
            applyTheme(props.scope.theme)
        }
    })

    return (
        <Show when={props.scope.initialized} fallback={<Booting />}>
            {/*
                Sem corpo AINDA e a marca, e nao o aviso de erro: entre o `initialized`
                e o fim do primeiro flip existe um instante com o slot vazio, e gritar
                ali faria toda abertura do painel parecer defeito. Quando o corpo
                EXISTE e nao ha view para ele, ai sim - e isso e o `fallback` do slot.
            */}
            <Show when={props.scope.body} fallback={<Booting />}>
                {(body) => <ViewSlot scope={body()} fallback={<CannotOpen />} />}
            </Show>

            {/*
                O dialogo e o aviso vao POR CIMA do corpo, e nao no lugar dele: sao
                slots proprios da casca. Quem os abre e um place (o dialogo) ou uma
                acao (o aviso), e nenhum dos dois substitui a tela embaixo.
            */}
            <ViewSlot scope={props.scope.dialog} fallback={null} />
            <Show when={props.scope.alert}>{(alert) => <AlertView scope={alert()} />}</Show>
        </Show>
    )
}

function Booting(): JSX.Element {
    return (
        <div class={Css.booting}>
            <Brand class={Css.bootingBrand} />
        </div>
    )
}

/**
 * O corpo nao pode ser desenhado - e isso e DEFEITO, e nao um estado previsto.
 *
 * Ela existiu como "tela ainda sem desenho em Solid", e aquilo era verdade enquanto
 * este renderizador convivia com o de React: a maior parte do painel estava por
 * portar, e o aviso mandava quem chegasse para o outro renderizador. O de React saiu
 * em 16/09/2026 e a paridade fechou em 15/09 - hoje toda rota viva tem view, entao
 * cair aqui quer dizer uma destas duas:
 *
 * - um scope sem view registrada (`view-catalog.ts`), que e erro de programacao; ou
 * - um endereco que nao resolveu place nenhum - link antigo, rota renomeada.
 *
 * Por isso ela GRITA no console alem de falar na tela: o texto antigo fazia um
 * defeito passar por etapa planejada, e ninguem vai atras do que parece combinado.
 */
function CannotOpen(): JSX.Element {
    console.error('[views] Nenhuma view para o corpo da aplicação - endereço inválido ou scope sem view registrada')

    return (
        <div class={Css.cannotOpen}>
            <Brand class={Css.cannotOpenBrand} />
            <p class={Css.cannotOpenTitle}>Não foi possível abrir esta tela</p>
            <p class={Css.cannotOpenText}>
                O endereço pode ser antigo, ou a tela pode ter mudado de lugar. Voltar ao início resolve na maioria dos
                casos.
            </p>
            <a href="#/" class={Css.back}>
                Voltar ao início
            </a>
        </div>
    )
}
